import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_ENDPOINTS } from './config';
import { FiShield, FiCheckCircle, FiClock, FiFileText, FiUser, FiAlertTriangle, FiHash } from 'react-icons/fi';
import SigningLoader from './SigningLoader';
import './App.css';

const VerifyDocument = () => {
  const [searchParams] = useSearchParams();
  const refId = searchParams.get('ref');
  const navigate = useNavigate();

  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!refId) {
      setError('No Reference ID provided.');
      setLoading(false);
      return;
    }

    // Determine Base URL from existing config
    const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];

    axios.get(`${baseUrl}/requests/get_document_by_reference/${refId}`)
      .then(res => {
        setDoc(res.data);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setError('Document not found or invalid reference ID.');
        setLoading(false);
      });
  }, [refId]);

  if (loading) {
    return (
      <div className="loader-container" style={{ height: '100vh', justifyContent: 'center', background: '#000000' }}>
        <SigningLoader size="lg" label="Verifying Cryptographic Signature..." />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#000000', padding: 20 }}>
        <div style={{ background: '#1a1a1a', padding: 40, borderRadius: 16, boxShadow: '0 4px 6px -1px rgba(0,0,0,0.5)', border: '1px solid #333', maxWidth: 450, width: '100%', textAlign: 'center' }}>
          <div style={{ background: 'rgba(239, 68, 68, 0.2)', width: 60, height: 60, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', border: '1px solid rgba(239, 68, 68, 0.4)' }}>
            <FiAlertTriangle size={30} color="#f87171" />
          </div>
          <h2 style={{ color: '#f3f4f6', marginBottom: 10 }}>Verification Failed</h2>
          <p style={{ color: '#9ca3af', marginBottom: 20 }}>{error}</p>
          <button className="btn btn-primary" onClick={() => navigate('/login')} style={{ width: '100%' }}>
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  const isSigned = doc.status === 'Signed';

  // Styles specific to this page (dark theme)
  const styles = {
    page: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #000000 0%, #0f0f0f 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 20,
      fontFamily: "'Inter', sans-serif"
    },
    card: {
      background: '#1a1a1a',
      borderRadius: 16,
      width: '100%',
      maxWidth: 600,
      overflow: 'hidden',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
      border: '1px solid #333'
    },
    header: {
      background: isSigned ? 'rgba(16, 185, 129, 0.15)' : 'rgba(245, 158, 11, 0.15)',
      padding: '40px 30px',
      textAlign: 'center',
      borderBottom: '1px solid #333'
    },
    iconBadge: {
      width: 80, height: 80, borderRadius: '50%',
      background: '#262626',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      margin: '0 auto 20px',
      border: isSigned ? '1px solid rgba(16, 185, 129, 0.4)' : '1px solid rgba(245, 158, 11, 0.4)'
    },
    body: { padding: 30 },
    sectionTitle: {
      fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
      color: '#9ca3af', letterSpacing: '1px', marginBottom: 15
    },
    row: {
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '12px 0', borderBottom: '1px solid #333'
    },
    label: { color: '#9ca3af', fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 8 },
    value: { color: '#f3f4f6', fontSize: 13, fontWeight: 600, textAlign: 'right' },
    hashBox: {
      background: '#262626', padding: 10, borderRadius: 6,
      fontFamily: 'monospace', fontSize: 11, color: '#d1d5db',
      border: '1px solid #404040', wordBreak: 'break-all', marginTop: 5
    },
    signerCard: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: 12, background: '#262626', borderRadius: 8, marginBottom: 8,
        border: '1px solid #333'
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        {/* HEADER */}
        <div style={styles.header}>
            <div style={styles.iconBadge}>
                {isSigned ? <FiShield size={40} color="#16a34a" /> : <FiClock size={40} color="#d97706" />}
            </div>
            <h1 style={{ fontSize: 24, fontWeight: 800, color: '#f3f4f6', marginBottom: 8 }}>
                {isSigned ? 'Document Valid' : 'Verification Pending'}
            </h1>
            <p style={{ color: '#9ca3af', fontSize: 14, margin: 0 }}>
                {isSigned
                    ? 'This document is authentic and has not been tampered with.'
                    : 'This document is currently active but the signing process is incomplete.'}
            </p>
        </div>

        {/* METADATA */}
        <div style={styles.body}>
            <div style={{ marginBottom: 30 }}>
                <div style={styles.sectionTitle}>Document Integrity</div>

                <div style={styles.row}>
                    <span style={styles.label}><FiFileText /> Document Name</span>
                    <span style={{...styles.value, maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                        {doc.file_path ? doc.file_path.split('/').pop().replace(/^\d+_/, '') : 'Unknown'}
                    </span>
                </div>

                <div style={styles.row}>
                    <span style={styles.label}><FiHash /> Reference ID</span>
                    <span style={{...styles.value, fontFamily: 'monospace', fontSize: 14}}>{doc.reference_id}</span>
                </div>

                <div style={styles.row}>
                    <span style={styles.label}><FiClock /> Created Date</span>
                    <span style={styles.value}>
                        {new Date(doc.created_at).toLocaleDateString()} {new Date(doc.created_at).toLocaleTimeString()}
                    </span>
                </div>

                <div style={{marginTop: 15}}>
                    <span style={styles.label}>Electronic Fingerprint (Hash)</span>
                    <div style={styles.hashBox}>
                        {/* We use a placeholder for display if not explicitly stored, or the ref ID as proof token */}
                        SECURE-TOKEN-{doc._id}-{doc.reference_id}
                    </div>
                </div>
            </div>

            {/* SIGNERS */}
            <div>
                <div style={styles.sectionTitle}>Chain of Custody</div>
                {doc.recipients.map((r, i) => (
                    <div key={r.email || i} style={styles.signerCard}>
                        <div style={{display:'flex', alignItems:'center', gap: 12}}>
                            <div style={{
                                width: 32, height: 32, borderRadius: '50%', background: '#404040',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af'
                            }}>
                                <FiUser size={16} />
                            </div>
                            <div>
                                <div style={{fontSize: 13, fontWeight: 700, color: '#e5e7eb'}}>{r.name}</div>
                                <div style={{fontSize: 11, color: '#9ca3af'}}>{r.email}</div>
                            </div>
                        </div>
                        {r.status === 'Signed' ? (
                            <div style={{display:'flex', alignItems:'center', gap: 4, color: '#34d399', fontSize: 12, fontWeight: 600}}>
                                <FiCheckCircle /> Signed
                            </div>
                        ) : (
                            <div style={{display:'flex', alignItems:'center', gap: 4, color: '#fbbf24', fontSize: 12, fontWeight: 600}}>
                                <FiClock /> Pending
                            </div>
                        )}
                    </div>
                ))}
            </div>

            <div style={{marginTop: 30, textAlign: 'center', borderTop: '1px solid #333', paddingTop: 20}}>
                <p style={{fontSize: 11, color: '#6b7280', margin: 0}}>
                    Verified by SignTada Secure Platform • Immutable Record
                </p>
            </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyDocument;